import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { memo, type ReactElement, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { suiteExecutionsGridConfig } from '@/features/executions';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { CLONE_TABLE_ROW_HEIGHT } from '@/features/shared/components/cloneTable/cloneTableConstants';
import { cloneTableSx } from '@/features/shared/components/cloneTable/cloneTableSx';
import { resolveEntityTags } from '@/features/shared/tags';
import { CloneCellContainer } from '@/features/tests/components/testsColumns.cloneCells';
import { useTagOptions } from '@/shared/hooks';
import { r, s, typographyTokens } from '@/theme/tokens';
import { Chip, formatDuration, formatStatusLabel, Inline, Link, Text, Tooltip } from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { getStatusIconComponent, RunLinkCell, StackedCountBar } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  Box,
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridSortModel,
  resolveStatusChipTone,
  type Theme,
} from '@/ui/primitives';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from './suitesColumns.latestCellStyles';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteExecutionDto } from '@flowqa/shared';
import type { QueryKey } from '@tanstack/react-query';

type SuiteExecutionsTableCloneProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteExecutionDto>>;
  queryKey?: QueryKey;
  initialData?: QueryResult<SuiteExecutionDto>;
  onRowsChange?: (rows: SuiteExecutionDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  disableLinks?: boolean;
  search?: string;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  tagColorByName?: Partial<Record<string, TagColor>>;
  hideNameColumn?: boolean;
  runColumnFirst?: boolean;
};

const EXECUTION_SUITE_TO_TAGS_GAP = s('xs');
const EXECUTION_META_DETAIL_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: s('xs'),
  pl: s('xxs'),
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
} as const;
const EXECUTION_META_DETAIL_VALUE_SX = {
  display: 'block',
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;
const EXECUTION_META_DETAIL_TEXT_SX = {
  '&&': {
    fontSize: '12px',
    fontWeight: typographyTokens.captionSmall.fontWeight,
    lineHeight: 1.05,
  },
} as const;
const EXECUTION_DETAILS_LINK_SX = {
  display: 'inline-block',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '12px !important',
  fontWeight: `${typographyTokens.captionSmall.fontWeight} !important`,
  lineHeight: '1.05 !important',
} as const;
const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: (theme: Theme) => theme.typography.body2.fontSize,
      lineHeight: (theme: Theme) => theme.typography.body2.lineHeight,
    },
  },
} as const;

const SuiteExecutionsTableCloneComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  noRowsText = 'No suite executions found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  disableLinks = false,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  tagColorByName = {},
  hideNameColumn = false,
  runColumnFirst = false,
}: SuiteExecutionsTableCloneProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const resolvedTagColorByName = useMemo(
    () =>
      tagOptions.reduce<Record<string, TagColor>>((acc, option) => {
        acc[option.value] = option.color;
        acc[option.value.trim().toLowerCase()] = option.color;
        return acc;
      }, {}),
    [tagOptions],
  );
  const mergedTagColorByName = useMemo(
    () => ({ ...resolvedTagColorByName, ...tagColorByName }),
    [resolvedTagColorByName, tagColorByName],
  );

  const columns = useMemo<Array<GridColDef<SuiteExecutionDto>>>(
    () => [
      {
        field: 'name',
        headerName: 'Suite',
        flex: 1.35,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'tests-table-clone-test-cell',
        valueGetter: (_value, row) => row.suiteName ?? row.name ?? row.suiteId,
        renderCell: (params) => {
          const tags = resolveEntityTags(params.row);
          const suiteHref = disableLinks
            ? null
            : `/suites/${params.row.suiteSlug ?? params.row.suiteId}`;
          return (
            <CloneCellContainer gap="none" pt="4px">
              {suiteHref ? (
                <Text role="body" sx={{ width: '100%', minWidth: 0 }}>
                  <Link
                    component={RouterLink}
                    to={suiteHref}
                    underline="hover"
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedProjectId(params.row.projectId);
                    }}
                    sx={{
                      display: 'inline-block',
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {params.row.suiteName ?? params.row.name ?? params.row.suiteId}
                  </Link>
                </Text>
              ) : (
                <Text role="body" truncate sx={{ display: 'block', width: '100%', minWidth: 0 }}>
                  {params.row.suiteName ?? params.row.name ?? params.row.suiteId}
                </Text>
              )}
              {tags.length > 0 ? (
                <Box sx={{ width: '100%', minWidth: 0, mt: EXECUTION_SUITE_TO_TAGS_GAP }}>
                  <Inline gap="sm" wrap="wrap" align="center" sx={{ width: '100%', minWidth: 0 }}>
                    {tags.map((tag) => {
                      const trimmed = tag.trim();
                      const fallback = trimmed.toLowerCase();
                      const tone =
                        mergedTagColorByName[trimmed] ??
                        mergedTagColorByName[fallback] ??
                        'neutral';
                      return (
                        <Chip
                          key={tag}
                          size="xs"
                          tone={tone}
                          label={tag}
                          sx={{ borderRadius: r('pill') }}
                        />
                      );
                    })}
                  </Inline>
                </Box>
              ) : null}
            </CloneCellContainer>
          );
        },
      },
      {
        field: 'runNumber',
        headerName: 'Run',
        flex: 0.55,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'tests-table-clone-test-cell',
        renderCell: (params) => {
          const runLabel =
            typeof params.row.runNumber === 'number' ? `#${params.row.runNumber}` : 'View';
          const href =
            (params.row.suiteSlug ?? params.row.suiteId)
              ? `/suites/${params.row.suiteSlug ?? params.row.suiteId}/runs/${params.row.runNumber ?? 1}`
              : '/suites';
          return (
            <CloneCellContainer gap="none" pt="4px">
              <RunLinkCell
                label={runLabel}
                href={disableLinks ? null : href}
                latest={params.row.isLatest === true}
                onClick={() => {
                  setSelectedProjectId(params.row.projectId);
                }}
              />
            </CloneCellContainer>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.68,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        cellClassName: 'clone-execution-status-cell',
        renderCell: (params) => {
          const status = params.row.status;
          const statusTone = resolveStatusChipTone(status);
          const statusLabel = formatStatusLabel(status) || '-';
          const suiteRunHref =
            !disableLinks && (params.row.suiteSlug ?? params.row.suiteId)
              ? `/suites/${params.row.suiteSlug ?? params.row.suiteId}/runs/${
                  params.row.runNumber ?? 1
                }`
              : null;
          const durationLabel = formatDuration(params.row.durationMs ?? null);
          const timestamp = params.row.timestamp;
          const timestampLabel = formatRelativeTime(timestamp);
          const timestampTitle = formatDate(timestamp, { includeTime: true });
          const resolvedTimestampTitle = timestampTitle || timestamp.toISOString();
          return (
            <CloneCellContainer gap="none" pt="5px">
              <Chip
                size="xs"
                icon={<Box component={getStatusIconComponent(status)} />}
                label={
                  suiteRunHref ? (
                    <Link
                      component={RouterLink}
                      to={suiteRunHref}
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedProjectId(params.row.projectId);
                      }}
                    >
                      {statusLabel}
                    </Link>
                  ) : (
                    statusLabel
                  )
                }
                tone={resolveStatusChipTone(status)}
                sx={{
                  mt: s('none'),
                  mb: `calc(${s('sm')} + ${s('xxs')})`,
                  borderRadius: r('pill'),
                  backgroundColor: (theme) => resolveLatestChipBackground(theme, statusTone),
                  color: (theme) => resolveLatestChipForeground(theme, statusTone),
                  '& .MuiChip-label': {
                    color: 'inherit',
                    fontWeight: 500,
                    fontSize: '13px !important',
                  },
                  '& .MuiChip-label .MuiTypography-root': {
                    color: 'inherit',
                    fontWeight: 'inherit',
                    fontSize: '13px !important',
                  },
                  '& .MuiChip-label a': {
                    color: 'inherit',
                    fontWeight: 'inherit',
                    fontSize: '13px !important',
                  },
                  '& .MuiChip-label *': {
                    fontSize: '13px !important',
                  },
                  '& .MuiChip-icon': {
                    color: 'inherit',
                    fontSize: (theme) => theme.app.iconSizes.sm,
                    marginRight: '-6px',
                  },
                }}
              />
              <Text
                role="captionSmall"
                color="textSecondary"
                dataGridTypography="custom"
                sx={EXECUTION_META_DETAIL_TEXT_SX}
              >
                <Box component="span" sx={EXECUTION_META_DETAIL_ROW_SX}>
                  <Tooltip title="Duration" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                    <TimerOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  </Tooltip>
                  <Box component="span" sx={EXECUTION_META_DETAIL_VALUE_SX}>
                    {durationLabel}
                  </Box>
                </Box>
              </Text>
              <Text
                role="captionSmall"
                color="textSecondary"
                dataGridTypography="custom"
                sx={[EXECUTION_META_DETAIL_TEXT_SX, { mt: s('xs') }]}
              >
                <Box component="span" sx={EXECUTION_META_DETAIL_ROW_SX}>
                  <Tooltip title="Executed" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                    <EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  </Tooltip>
                  <Box component="span" sx={EXECUTION_META_DETAIL_VALUE_SX}>
                    <Tooltip
                      title={resolvedTimestampTitle}
                      arrow
                      slotProps={CLONE_TOOLTIP_SLOT_PROPS}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          width: '100%',
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {timestampLabel}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              </Text>
            </CloneCellContainer>
          );
        },
      },
      {
        field: 'tests',
        headerName: 'Tests',
        flex: 1.3,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const total = params.row.tests ?? 0;
          const failures = params.row.failures ?? 0;
          const errors = params.row.errors ?? 0;
          const skipped = params.row.skipped ?? 0;
          const passed = Math.max(total - failures - errors - skipped, 0);
          const segments = [
            { label: 'Passed', value: passed, color: '#43A047' },
            { label: 'Failed', value: failures, color: '#EF5350' },
            { label: 'Errored', value: errors, color: '#8D6E63' },
            { label: 'Skipped', value: skipped, color: '#FB8C00' },
          ];
          return (
            <CloneCellContainer gap="none" pt="0px">
              <Text role="body" color="textSecondary">
                {`${total} test${total === 1 ? '' : 's'}`}
              </Text>
              <Box sx={{ width: '100%', pt: s('xs') }}>
                <StackedCountBar segments={segments} total={total} />
              </Box>
            </CloneCellContainer>
          );
        },
      },
      {
        field: 'runId',
        headerName: 'Details',
        flex: 0.95,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const uploadedLabel = formatRelativeTime(params.row.createdAt);
          const uploadedTitle = formatDate(params.row.createdAt, { includeTime: true });
          const resultsFileLabel =
            params.row.resultsFile?.fileName ?? params.row.resultsFileId ?? '-';
          const resultsFileHref = params.row.resultsFileId
            ? `/results-files/${params.row.resultsFileId}`
            : null;
          return (
            <Box sx={{ height: '100%', pt: '6px', boxSizing: 'border-box' }}>
              <CloneCellContainer gap="none" pt="0px">
                <Text
                  role="captionSmall"
                  color="textSecondary"
                  dataGridTypography="custom"
                  sx={EXECUTION_META_DETAIL_TEXT_SX}
                >
                  <Box component="span" sx={EXECUTION_META_DETAIL_ROW_SX}>
                    <Tooltip title="Execution Run" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                      <FingerprintOutlinedIcon
                        sx={{ fontSize: (theme) => theme.app.iconSizes.sm }}
                      />
                    </Tooltip>
                    <Box component="span" sx={EXECUTION_META_DETAIL_VALUE_SX}>
                      {disableLinks ? (
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {params.row.runId}
                        </Box>
                      ) : (
                        <Link
                          component={RouterLink}
                          to={`/execution-runs/${params.row.runId}`}
                          underline="hover"
                          color="inherit"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          sx={EXECUTION_DETAILS_LINK_SX}
                        >
                          {params.row.runId}
                        </Link>
                      )}
                    </Box>
                  </Box>
                </Text>
                <Text
                  role="captionSmall"
                  color="textSecondary"
                  dataGridTypography="custom"
                  sx={[EXECUTION_META_DETAIL_TEXT_SX, { mt: s('xs') }]}
                >
                  <Box component="span" sx={EXECUTION_META_DETAIL_ROW_SX}>
                    <Tooltip title="Results File" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                      <DescriptionOutlinedIcon
                        sx={{ fontSize: (theme) => theme.app.iconSizes.sm }}
                      />
                    </Tooltip>
                    <Box component="span" sx={EXECUTION_META_DETAIL_VALUE_SX}>
                      {disableLinks || resultsFileHref === null ? (
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {resultsFileLabel}
                        </Box>
                      ) : (
                        <Link
                          component={RouterLink}
                          to={resultsFileHref}
                          underline="hover"
                          color="inherit"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          sx={EXECUTION_DETAILS_LINK_SX}
                        >
                          {resultsFileLabel}
                        </Link>
                      )}
                    </Box>
                  </Box>
                </Text>
                <Text
                  role="captionSmall"
                  color="textSecondary"
                  dataGridTypography="custom"
                  sx={[EXECUTION_META_DETAIL_TEXT_SX, { mt: s('xs') }]}
                >
                  <Box component="span" sx={EXECUTION_META_DETAIL_ROW_SX}>
                    <Tooltip title="Uploaded" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                      <EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                    </Tooltip>
                    <Box component="span" sx={EXECUTION_META_DETAIL_VALUE_SX}>
                      <Tooltip
                        title={uploadedTitle || ''}
                        arrow
                        slotProps={CLONE_TOOLTIP_SLOT_PROPS}
                        disableHoverListener={!uploadedTitle}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {uploadedLabel || '-'}
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                </Text>
              </CloneCellContainer>
            </Box>
          );
        },
      },
    ],
    [disableLinks, mergedTagColorByName, setSelectedProjectId],
  );
  const resolvedColumns = useMemo(() => {
    const visibleColumns = hideNameColumn
      ? columns.filter((column) => column.field !== 'name')
      : columns;
    if (!runColumnFirst) {
      return visibleColumns;
    }
    const runColumn = visibleColumns.find((column) => column.field === 'runNumber');
    const remainingColumns = visibleColumns.filter((column) => column.field !== 'runNumber');
    return runColumn ? [runColumn, ...remainingColumns] : visibleColumns;
  }, [columns, hideNameColumn, runColumnFirst]);
  const togglableColumnFields = useMemo(
    () => resolvedColumns.map((column) => column.field).filter((field) => field !== 'name'),
    [resolvedColumns],
  );
  const gridConfig = suiteExecutionsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'suite-executions-table-clone' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });
  const gridProps = buildTableGridProps<SuiteExecutionDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
    showToolbar,
    chrome: 'embedded',
  });

  return (
    <ServerDataTable<SuiteExecutionDto>
      queryPath="suite-executions/query"
      {...gridConfig}
      {...gridProps}
      initialData={initialData}
      columns={resolvedColumns}
      getRowId={(row) => row.id}
      token={session?.tokens.accessToken ?? null}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      search={search}
      columnVisibilityModel={columnVisibilityModel}
      disableColumnMenu={disableColumnMenu}
      disableColumnSorting={disableColumnSorting}
      disableCellFocus
      getRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
      skeletonGetRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
      showSkeletonOnFetch
      autoHeight
      gridProps={{
        onColumnVisibilityModelChange,
        slotProps: {
          toolbar: {
            showQuickFilter: true,
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          },
          columnsManagement: {
            disableShowHideToggle: true,
            disableResetButton: true,
            getTogglableColumns: () => togglableColumnFields,
          },
        },
      }}
      sx={[
        ...(Array.isArray(cloneTableSx) ? cloneTableSx : [cloneTableSx]),
        {
          '& .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="tests"], & .MuiDataGrid-cell[data-field="runNumber"], & .MuiDataGrid-cell[data-field="runId"]':
            {
              alignItems: 'flex-start',
            },
        },
      ]}
    />
  );
};

export const SuiteExecutionsTableClone = memo(SuiteExecutionsTableCloneComponent);
